import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        mobile: '',
        otp: '',
        userOtp: ''
    });
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [otpTimer, setOtpTimer] = useState(30);
    const [timerActive, setTimerActive] = useState(false);
    const [error, setError] = useState('');

    // OTP Timer Effect
    useEffect(() => {
        let interval;
        if (timerActive && otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer(prev => prev - 1);
            }, 1000);
        } else if (otpTimer === 0) {
            setTimerActive(false);
        }
        return () => clearInterval(interval);
    }, [timerActive, otpTimer]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(''); // Clear error when user types
    };

    // Validate mobile number
    const validateMobile = (mobile) => {
        if (!mobile.trim()) {
            setError("Please enter your mobile number");
            return false;
        }
        if (!/^\d{10}$/.test(mobile)) {
            setError("Please enter a valid 10-digit mobile number");
            return false;
        }
        return true;
    };

    // Validate OTP
    const validateOtp = (otp) => {
        if (!otp.trim()) {
            setError("Please enter the OTP");
            return false;
        }
        if (!/^\d{6}$/.test(otp)) {
            setError("Please enter a valid 6-digit OTP");
            return false;
        }
        return true;
    };

    // Send OTP request
    const sendOtp = async () => {
        if (!validateMobile(formData.mobile)) return;

        setLoading(true);
        setError('');
        
        try {
            const submitData = new FormData();
            submitData.append('mobile', formData.mobile);

            const response = await axios.post(
                'https://octafxx.in/Rest/web_forgot_password_api/', 
                submitData,
                { timeout: 30000 }
            );

            if (response.data.status === 0) {
                setFormData(prev => ({ ...prev, otp: response.data.otp }));
                setCurrentStep(2);
                setOtpTimer(30);
                setTimerActive(true);
            } else {
                setError(response.data.msg || "Failed to send OTP. Please try again.");
            }
        } catch (error) {
            console.error('OTP send error:', error);
            if (error.response) {
                setError(error.response.data.msg || "Failed to send OTP. Please try again.");
            } else if (error.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Verify OTP
    const verifyOtp = async () => {
        if (!validateOtp(formData.userOtp)) return;

        if (formData.userOtp != formData.otp) {
            setError("Incorrect OTP. Please check and try again.");
            return;
        }

        setLoading(true);
        
        try {
            // If OTP is correct, navigate to reset password page
            navigate('/resetpassword', { 
                state: { mobile: formData.mobile } 
            });
        } catch (error) {
            console.error('OTP verification error:', error);
            setError("Verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP
    const resendOtp = async () => {
        setLoading(true);
        setError('');
        
        try {
            const submitData = new FormData();
            submitData.append('mobile', formData.mobile);

            const response = await axios.post(
                'https://octafxx.in/Rest/forgot_password_api/', 
                submitData,
                { timeout: 30000 }
            );

            if (response.data.status === 0) {
                setFormData(prev => ({ ...prev, otp: response.data.otp, userOtp: '' }));
                setOtpTimer(30);
                setTimerActive(true);
                setError(''); // Clear any previous errors
            } else {
                setError(response.data.msg || "Failed to resend OTP. Please try again.");
            }
        } catch (error) {
            console.error('Resend OTP error:', error);
            if (error.response) {
                setError(error.response.data.msg || "Failed to resend OTP. Please try again.");
            } else if (error.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Format timer display
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-vh-100 bg-gradient-primary">
            <Helmet>
                <title>Reset Password | AUROFX MARKET</title>
                <meta name="description" content="Reset your AUROFX MARKET account password securely" />
            </Helmet>

            {/* Header */}
            <div className="bg-transparent">
                <div className="container-fluid py-4">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <Link to="/login" className="btn btn-outline-light btn-sm">
                                <i className="fas fa-arrow-left me-2"></i>
                                Back to Login
                            </Link>
                        </div>
                        <div className="col text-center">
                            <h4 className="mb-0 text-white fw-bold">Reset Password</h4>
                        </div>
                        <div className="col-auto">
                            <div className="text-white opacity-75">
                                <small>Step {currentStep} of 2</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress Steps */}
            <div className="container-fluid mb-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        <div className="progress-steps">
                            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                                <div className="step-icon">1</div>
                                <span className="step-label">Verify Mobile</span>
                            </div>
                            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                                <div className="step-icon">2</div>
                                <span className="step-label">Enter OTP</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid pb-5">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        {/* Loading Overlay */}
                        {loading && (
                            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50" style={{zIndex: 9999}}>
                                <div className="text-center">
                                    <div className="spinner-border text-light mb-3" style={{width: '3rem', height: '3rem'}}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="text-white mb-0">
                                        {currentStep === 1 ? 'Sending OTP...' : 'Verifying OTP...'}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="card border-0 shadow-lg">
                            <div className="card-body p-4 p-md-5">
                                <div className="text-center mb-4">
                                    <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                                         style={{width: '80px', height: '80px'}}>
                                        <i className="fas fa-lock fa-2x text-warning"></i>
                                    </div>
                                    <h2 className="fw-bold text-dark mb-2">Reset Your Password</h2>
                                    <p className="text-muted">
                                        {currentStep === 1 
                                            ? 'Enter your registered mobile number to receive OTP' 
                                            : 'Enter the OTP sent to your mobile number'
                                        }
                                    </p>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="alert alert-danger border-0 d-flex align-items-center">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {currentStep === 1 ? (
                                    /* Step 1: Mobile Verification */
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-mobile-alt text-primary me-2"></i>
                                                Mobile Number *
                                            </label>
                                            <input
                                                type="tel"
                                                className="form-control form-control-lg"
                                                value={formData.mobile}
                                                onChange={(e) => handleInputChange('mobile', e.target.value.replace(/\D/g, ''))}
                                                placeholder="Enter 10-digit mobile number"
                                                maxLength="10"
                                                disabled={loading}
                                            />
                                            <div className="form-text">
                                                We'll send a verification code to this number
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <button
                                                onClick={sendOtp}
                                                className="btn btn-primary btn-lg w-100 py-3 fw-bold"
                                                disabled={loading || formData.mobile.length !== 10}
                                            >
                                                <i className="fas fa-paper-plane me-2"></i>
                                                SEND OTP
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    /* Step 2: OTP Verification */
                                    <div className="row g-3">
                                        <div className="col-12">
                                            {/* Mobile Number Display */}
                                            <div className="alert alert-info border-0">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-mobile-alt me-2"></i>
                                                        <strong>{formData.mobile}</strong>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => setCurrentStep(1)}
                                                        disabled={loading}
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>

                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-shield-alt text-primary me-2"></i>
                                                Verification Code *
                                            </label>
                                            
                                            {/* OTP Input */}
                                            <div className="otp-container mb-3">
                                                {[...Array(6)].map((_, index) => (
                                                    <input
                                                        key={index}
                                                        type="text"
                                                        maxLength="1"
                                                        className="otp-input"
                                                        value={formData.userOtp[index] || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (/^\d?$/.test(value)) {
                                                                const newOtp = formData.userOtp.split('');
                                                                newOtp[index] = value;
                                                                handleInputChange('userOtp', newOtp.join(''));
                                                                
                                                                // Auto-focus next input
                                                                if (value && index < 5) {
                                                                    document.getElementById(`otp-${index + 1}`)?.focus();
                                                                }
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Backspace' && !formData.userOtp[index] && index > 0) {
                                                                document.getElementById(`otp-${index - 1}`)?.focus();
                                                            }
                                                        }}
                                                        id={`otp-${index}`}
                                                        disabled={loading}
                                                    />
                                                ))}
                                            </div>

                                            {/* Timer and Resend */}
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                {timerActive ? (
                                                    <div className="text-muted">
                                                        <i className="fas fa-clock me-1"></i>
                                                        Resend OTP in {formatTimer(otpTimer)}
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link text-primary p-0 text-decoration-none"
                                                        onClick={resendOtp}
                                                        disabled={loading}
                                                    >
                                                        <i className="fas fa-redo me-1"></i>
                                                        Resend OTP
                                                    </button>
                                                )}
                                                
                                                <div className="text-muted small">
                                                    {formData.userOtp.length}/6 digits
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <button
                                                onClick={verifyOtp}
                                                className="btn btn-success btn-lg w-100 py-3 fw-bold"
                                                disabled={loading || formData.userOtp.length !== 6}
                                            >
                                                <i className="fas fa-check-circle me-2"></i>
                                                VERIFY & CONTINUE
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Back to Login */}
                                <div className="text-center mt-4">
                                    <p className="text-muted mb-0">
                                        Remember your password?{' '}
                                        <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                                            Back to Login
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}